// Sign In Page Component
// Implements email/password authentication with form validation
// Enhanced with comprehensive accessibility features

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSignIn } from '../hooks/useAuth';
import { signInSchema, type SignInData } from '../lib/schemas';
import { getErrorMessage } from '../lib/firebase-errors';
import {
  FormField,
  FormButton,
  FormError,
  FormSuccess,
  SkipLinks,
} from '../components/form';
import { useFocusManagement, useErrorFocus } from '../hooks/useFocusManagement';
import { announceToScreenReader } from '../lib/accessibility';

export function SignInPage() {
  const signInMutation = useSignIn();
  // Read so the links out can carry the pending destination onward, and for
  // the relying party's `login_hint`.
  const location = useLocation();
  const navigate = useNavigate();
  const loginHint = (location.state as { loginHint?: string } | null)
    ?.loginHint;
  // Set by the sign-up page when the account was created but the sign-in
  // straight after it failed (#2123). Read ONCE, into state: the effect below
  // removes the key from history, and the notice has to outlive that.
  // `location.state` is `unknown` at runtime, so the type is checked.
  const [accountCreatedEmail] = useState<string | undefined>(() => {
    const value = (location.state as { accountCreatedEmail?: unknown } | null)
      ?.accountCreatedEmail;
    return typeof value === 'string' ? value : undefined;
  });
  const [globalError, setGlobalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<SignInData>({
    resolver: zodResolver(signInSchema),
    // See SignUpPage: prefilled from the relying party's `login_hint`. It
    // matters on sign-in too — a returning user who types a second address of
    // theirs signs in perfectly well and arrives at the partner as somebody
    // with no subscription.
    //
    // The address of an account created a moment ago comes first: it is the
    // one that is known to have an account, and the hint may be another.
    defaultValues: { email: accountCreatedEmail ?? loginHint },
  });

  // Remove `accountCreatedEmail` from history state once it has been read, so
  // a reload or a return to this entry does not announce a new account again.
  // Only that key: `returnTo` is how `PublicRoute` finds a pending
  // relying-party sign-in after the person signs in, and `loginHint` is the
  // partner's. After the replace the key is absent and this does nothing.
  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    if (!state || !('accountCreatedEmail' in state)) {
      return;
    }
    const rest = { ...state };
    delete rest.accountCreatedEmail;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      // Null rather than `{}` when nothing else was there: the links out
      // forward this state as it is.
      state: Object.keys(rest).length > 0 ? rest : null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  // Accessibility hooks
  const { containerRef } = useFocusManagement({
    autoFocusFirst: true,
    restoreFocus: true,
  });
  const { focusFirstError } = useErrorFocus();

  // Focus first error when form validation fails
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      focusFirstError();
      // Announce errors to screen readers
      const errorMessages = Object.values(errors)
        .map(error => error?.message)
        .filter(Boolean);
      if (errorMessages.length > 0) {
        announceToScreenReader(
          `Form has ${errorMessages.length} error${errorMessages.length === 1 ? '' : 's'}. ${errorMessages.join('. ')}`,
          'assertive'
        );
      }
    }
  }, [errors, focusFirstError]);

  // Announce submission status changes
  useEffect(() => {
    if (signInMutation.isPending) {
      announceToScreenReader('Signing in, please wait.', 'assertive');
    } else if (globalError) {
      announceToScreenReader(`Sign in failed: ${globalError}`, 'assertive');
    }
  }, [signInMutation.isPending, globalError]);

  const onSubmit = async (data: SignInData) => {
    try {
      setGlobalError(null);
      await signInMutation.mutateAsync(data);

      // Announce success
      announceToScreenReader('Sign in successful! Redirecting...', 'polite');

      // PublicRoute will handle the redirect based on returnTo state
      // No manual navigation needed here
    } catch (error) {
      console.error('[SignInPage] Sign in error:', error);
      const errorMessage = getErrorMessage(error);
      setGlobalError(errorMessage);

      // Focus back to first field for retry
      setTimeout(() => setFocus('email'), 100);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8"
    >
      {/* Skip Links for Keyboard Navigation */}
      <SkipLinks showMainContent={true} showForm={true} />

      {/* Page Header */}
      <header className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1
            id="main-content"
            className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight"
            tabIndex={-1}
          >
            Sign in to Heapster ID
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Sign in to your account
          </p>
        </div>
      </header>

      {/* Main Form Section */}
      <main className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <section
          className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200"
          aria-labelledby="form-heading"
          role="form"
        >
          <h2 id="form-heading" className="sr-only">
            Sign In Form
          </h2>

          <form
            id="main-form"
            onSubmit={e => void handleSubmit(onSubmit)(e)}
            className="space-y-6"
            noValidate
            aria-describedby={globalError ? 'form-error' : undefined}
          >
            {/* Information, not an error: nothing the person did went wrong.
                Gone once a sign-in attempt has failed, when the error is the
                thing to read. */}
            {accountCreatedEmail && !globalError && (
              <FormSuccess>
                Your account was created — please sign in.
              </FormSuccess>
            )}

            {globalError && (
              <div id="form-error">
                <FormError>{globalError}</FormError>
              </div>
            )}

            <FormField
              {...register('email')}
              label="Email Address"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              // Only when a relying party sent `login_hint`, and it is the
              // reason this field is already filled in. That address is the
              // one the PARTNER holds, which is not necessarily the one this
              // person's HeapsterID account uses (#1947) — and because it is
              // genuinely theirs, it looks right. Signing in with it fails
              // indistinguishably from a wrong password, and the exit most
              // people reach for is "create an account", which duplicates
              // them under the partner's address for good.
              //
              // Goes through `helpText` rather than a paragraph of our own so
              // it lands in the input's `aria-describedby`: after a failed
              // sign-in this page moves focus back to this very field, so a
              // screen-reader user hears the note exactly when they are
              // deciding what went wrong.
              //
              // Not when the field holds `accountCreatedEmail`: that address
              // is what the person typed at sign-up, so the note would be
              // untrue, and its advice would steer them off the one address
              // that has an account.
              helpText={
                loginHint && !accountCreatedEmail
                  ? 'This address was suggested by the application you are signing in to. If your HeapsterID account uses a different one, change it.'
                  : undefined
              }
              isLoading={signInMutation.isPending}
              aria-required="true"
            />

            <FormField
              {...register('password')}
              label="Password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              error={errors.password?.message}
              isLoading={signInMutation.isPending}
              aria-required="true"
            />

            <FormButton
              type="submit"
              isLoading={signInMutation.isPending}
              loadingText="Signing In..."
              aria-describedby="submit-help"
            >
              Sign In
            </FormButton>
            <div id="submit-help" className="sr-only">
              {signInMutation.isPending
                ? 'Sign in in progress, please wait'
                : 'Click to sign in to your account'}
            </div>
          </form>

          {/* Navigation Links */}
          <nav className="mt-6" aria-label="Authentication options">
            <div id="help-links" className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Don&apos;t have an account?{' '}
                <Link
                  to="/signup"
                  // Carry the pending destination through to sign-up. Without
                  // this, somebody part-way through signing in to a third-party
                  // site who realises they have no account arrives at sign-up
                  // with no returnTo, completes it, and lands on the dashboard
                  // — the relying party's request abandoned with no error
                  // anywhere. Moved in from #1831, which owns the other links
                  // that still drop it.
                  state={location.state}
                  className="font-medium text-sky-600 hover:text-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded transition-colors duration-200"
                  aria-describedby="signup-help"
                >
                  Create one here
                </Link>
                <span id="signup-help" className="sr-only">
                  Navigate to account creation page
                </span>
              </p>
              <p className="text-sm">
                <Link
                  to="/forgot-password"
                  // Same reason as "Create one here" above: without this the
                  // pending relying-party sign-in is dropped, and someone who
                  // tries a password, fails, and resets it finishes on the
                  // dashboard with the partner's request abandoned (#1831).
                  state={location.state}
                  className="font-medium text-sky-600 hover:text-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded transition-colors duration-200"
                  aria-describedby="forgot-help"
                >
                  Forgot your password?
                </Link>
                <span id="forgot-help" className="sr-only">
                  Navigate to password reset page
                </span>
              </p>
            </div>
          </nav>
        </section>
      </main>
    </div>
  );
}
